import React, { useState, useRef, useEffect } from 'react'
import { Search, ChevronDown } from 'lucide-react'
import { cn } from '@/utils'
import { Input } from '@/components/atoms'

export interface FilterOption {
  value: string
  label: string
}

export interface FilterGroup {
  id: string
  label: string
  options: FilterOption[]
  value: string
  onChange: (value: string) => void
}

export interface FiltersProps {
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  filterGroups?: FilterGroup[]
  className?: string
  showSearch?: boolean
  summary?: React.ReactNode
  layout?: 'horizontal' | 'grid'
}

const CustomSelect: React.FC<{
  options: FilterOption[]
  value: string
  onChange: (value: string) => void
  className?: string
}> = ({ options, value, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectedOption = options.find(opt => opt.value === value) || options[0]

  return (
    <div ref={selectRef} className={cn("relative", className)}>
      <div
        className={cn(
          'w-full flex items-center gap-[8px]',
          'bg-[rgba(255,255,255,0.52)] dark:bg-[#1c1c1c]',
          'border border-[rgba(45,41,35,0.12)] dark:border-[#1b1919]',
          'pl-[16px] pr-[12px] py-[17px]',
          'cursor-pointer rounded-md',
          'focus:outline-none transition-colors',
          'hover:bg-[rgba(255,255,255,0.52)] dark:hover:bg-[#1c1c1c]'
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        <p className={cn(
          'flex-1 min-w-0',
          'font-plus-jakarta font-normal leading-[18px] text-[13px]',
          'text-[#2d2923] dark:text-[#fdfdfd]',
          'whitespace-pre-wrap'
        )}>
          {selectedOption.label}
        </p>
        <ChevronDown 
          className={cn(
            "shrink-0 w-5 h-5 text-[#2d2923] dark:text-[#fdfdfd]",
            "transition-transform duration-200",
            isOpen && "rotate-180"
          )} 
        />
      </div>
      
      {isOpen && (
        <div className={cn(
          'absolute top-[calc(100%+4px)] left-0 w-full',
          'bg-white dark:bg-[#1c1c1c] border border-[rgba(45,41,35,0.12)] dark:border-[#1b1919] rounded-lg',
          'shadow-lg overflow-hidden z-50'
        )}>
          {options.map((option) => (
            <div
              key={option.value}
              className={cn(
                'px-3 py-2 cursor-pointer text-sm',
                'text-[#2d2923] dark:text-[#fdfdfd] transition-colors',
                option.value === value 
                  ? 'bg-[#ededed] dark:bg-[#0a0a0a]'
                  : 'hover:bg-[#f5f5f5] dark:hover:bg-[#0a0a0a]'
              )}
              onClick={() => {
                onChange(option.value)
                setIsOpen(false)
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export const Filters: React.FC<FiltersProps> = ({
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  filterGroups = [],
  className,
  showSearch = true,
  summary,
  layout = 'grid'
}) => {
  return (
    <div className={cn('space-y-4', className)}>
      {summary && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-muted-foreground">
            {summary}
          </div>
        </div>
      )}

      <div className={cn(
        layout === 'horizontal' 
          ? 'flex flex-nowrap items-center gap-[8px] w-full' 
          : 'flex flex-col gap-3 w-full'
      )}>
        {/* Search */}
        {showSearch && (
          <div className={cn(
            'relative',
            layout === 'horizontal' ? 'flex-[4] min-w-0' : 'w-full'
          )}>
            <Input
              type="text"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              leftElement={<Search size={16} className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]" />}
              variant="search"
            />
          </div>
        )}

        {/* Filter Groups */}
        {filterGroups.map((group) => (
          <CustomSelect
            key={group.id}
            options={group.options}
            value={group.value}
            onChange={group.onChange}
            className={cn(
              layout === 'horizontal' ? 'flex-1 min-w-0' : 'w-full'
            )}
          />
        ))}
      </div>
    </div>
  )
}

export default Filters 
